import * as React from "react";
import moment, { Moment } from "moment";
import { calendarSkeleton, formatData, getMaxTimeslots } from "./helpers";
import { HorizontalCalendar } from "components/design-system/organisms/Calendar";
import { Timeslot, FeasibleTimeslots } from "types/timeslot";

interface DesktopCalendarProps {
  data: FeasibleTimeslots;
  postalCode: string;

  // This will be removed when
  // replaced with the new query
  queryParams: any;
  onNext: (date: Moment) => void;
  onPrevious: (date: Moment) => void;
  onlyDoubleTimeslots: boolean;
  setTimeslot: (timeslot: Timeslot) => void;
  selectedTimeslot: Timeslot;
}
export const DesktopCalendar: React.FunctionComponent<DesktopCalendarProps> = ({
  data,
  postalCode,
  queryParams,
  onNext,
  onPrevious,
  onlyDoubleTimeslots,
  setTimeslot,
  selectedTimeslot,
}) => {
  const [dates, setDate] = React.useState(calendarSkeleton());
  const [nextDate, setNextDate] = React.useState(null);
  const [hasLoadedCalendar, setHasLoadedCalendar] = React.useState(false);

  React.useEffect(() => {
    setHasLoadedCalendar(false);
  }, [postalCode]);

  React.useEffect(() => {
    if (data) {
      setDate(calendarSkeleton(moment.unix(queryParams.beginning)));
    }
  }, [data, queryParams]);

  React.useEffect(() => {
    if (data) {
      const filteredTimeslots = data.timeslots.filter(
        (timeslot) => timeslot.total_capacity - timeslot.nb_bookings >= 2
      );
      const timeslotToReturn = onlyDoubleTimeslots ? filteredTimeslots : data.timeslots;
      const formattedData = timeslotToReturn.reduce(formatData, {});
      const maxTimeslots = getMaxTimeslots(formattedData);

      const formattedTimeslots = dates.map((date) => {
        const timeslots = formattedData[date.day] || [];
        const sortedTimeslots = [...timeslots, ...Array.from({ length: maxTimeslots - timeslots.length })].sort(
          // The type can't infered due
          // to array method misused
          // This will be removed entirely in a future PR
          (timeslotA: Timeslot, timeslotB: Timeslot) => {
            if (timeslotA.end < timeslotB.end) {
              return -1;
            }
            return 1;
          }
        );
        return { ...date, timeslots: sortedTimeslots };
      });
      const hasTimeslots = !formattedTimeslots.every((formattedTimeslot) => formattedTimeslot.timeslots.length === 0);

      setNextDate(data.next_timeslot?.beginning);
      if (!hasTimeslots && !hasLoadedCalendar) {
        onNext(moment(data.next_timeslot?.beginning));
      } else {
        setHasLoadedCalendar(true);
      }
    }
  }, [data, dates, hasLoadedCalendar, onNext, onlyDoubleTimeslots]);

  const handlePrevious = React.useCallback(() => {
    const previousStartDate = moment.unix(dates[0].day).subtract(3, "d");
    onPrevious(previousStartDate);
  }, [dates, onPrevious]);

  const handleNext = React.useCallback(() => {
    const nextStartDate = moment.unix(dates[2].day).add(1, "d");
    onNext(nextStartDate);
  }, [dates, onNext]);

  const handleNewDate = React.useCallback(() => {
    const nextStartDate = moment(nextDate);
    onNext(nextStartDate);
  }, [nextDate, onNext]);

  return (
    <HorizontalCalendar
      dates={dates}
      data={data}
      onPrevious={handlePrevious}
      onNext={handleNext}
      onSelectTimeslot={setTimeslot}
      onGoToNextAvailableTimeslot={handleNewDate}
      selectedTimeslot={selectedTimeslot}
    />
  );
};
